import React from 'react';
import { motion } from 'framer-motion';
import { Play, MapPin, CalendarClock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/Button';
import { useCountdown } from '../hooks/useCountdown';
import { SEASON_2_PREMIERE_ISO, season2Premiere } from '../data/season2Episodes';
import { SPOTIFY_SHOW_URL } from '../data/season1Episodes';
interface Season2AnnouncementProps {
  showDetailsLink?: boolean;
}
const premiereDate = new Date(SEASON_2_PREMIERE_ISO);
const premiereDay = premiereDate.toLocaleDateString('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  timeZone: 'America/New_York'
});
export function Season2Announcement({
  showDetailsLink = true
}: Season2AnnouncementProps) {
  const {
    days,
    hours,
    minutes,
    seconds,
    isLive
  } = useCountdown(SEASON_2_PREMIERE_ISO);
  const units = [{
    label: 'Days',
    value: days
  }, {
    label: 'Hours',
    value: hours
  }, {
    label: 'Min',
    value: minutes
  }, {
    label: 'Sec',
    value: seconds
  }];
  return <section aria-labelledby="season2-heading" className="relative bg-dark-900 border-y border-gold-500/30 py-20 md:py-28 overflow-hidden" data-id="element-118" data-mp="hc3wfez7ynqf">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-119" data-mp="r2yjumo7btdg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-120" data-mp="r4rra46n2hmi">
          {/* Copy + countdown */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-7 order-2 lg:order-1" data-id="element-121" data-mp="3ujf3rp1v0jeq">
            <p className="flex items-center gap-2 text-gold-500 font-semibold mb-5" data-id="element-122" data-mp="8ejrnw25e9b2">
              <span className="relative flex h-2.5 w-2.5" data-id="element-123" data-mp="d9795ayqi3g5">
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-500 opacity-75" data-id="element-124" data-mp="1jqavi1stqgrz"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold-500" data-id="element-125" data-mp="1osjqf3p9bswq"></span>
              </span>
              {isLive ? 'Now streaming' : 'The wait is almost over'}
            </p>

            <h2 id="season2-heading" className="text-5xl md:text-7xl font-serif font-bold text-white leading-[1.05] mb-6" data-id="element-126" data-mp="3fwlcsywe02ys">
              {isLive ? <>
                  Season 2 <span className="text-gold-500" data-id="element-127" data-mp="322uaaah2pjck">is here.</span>
                </> : <>
                  Season 2 <span className="text-gold-500" data-id="element-128" data-mp="2x5272kazaat2">premieres</span>
                  <br data-id="element-129" data-mp="3av5p0z2tricp" />
                  {premiereDay}.
                </>}
            </h2>

            <p className="flex items-center gap-2 text-lg md:text-xl text-white/80 mb-10" data-id="element-130" data-mp="3645s3ttph0a7">
              <CalendarClock className="w-5 h-5 text-gold-500 shrink-0" data-id="element-131" data-mp="2iev0p2y8wi8q" />
              3:00 PM Eastern · Spotify, Apple Podcasts & everywhere you listen
            </p>

            {!isLive && <div className="grid grid-cols-4 gap-3 max-w-lg mb-10" role="timer" aria-label={`${days} days, ${hours} hours, ${minutes} minutes until Season 2`} data-id="element-132" data-mp="249uoh880o1ju">
                {units.map(unit => <div key={unit.label} className="rounded-xl bg-dark-800 border border-dark-600 py-4 text-center" data-id="element-133" data-mp="1u9ai5r5n46ur">
                    <p className="text-3xl md:text-5xl font-serif font-bold text-white tabular-nums" data-id="element-134" data-mp="2n1dmt1r3irfj">
                      {String(unit.value).padStart(2, '0')}
                    </p>
                    <p className="text-xs uppercase tracking-wider text-white/50 mt-1" data-id="element-135" data-mp="2d0uzfx0bossv">
                      {unit.label}
                    </p>
                  </div>)}
              </div>}

            {/* Episode 1 teaser */}
            <div className="border-t border-dark-600 pt-8" data-id="element-136" data-mp="16lszcl4csj3x">
              <p className="text-sm text-white/50 mb-2" data-id="element-137" data-mp="16ndry573xrz5">
                Episode 1 · {season2Premiere.guest}'s Story
              </p>
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3" data-id="element-138" data-mp="1i28oi5fwose0">
                {season2Premiere.title}
              </h3>
              <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-2xl" data-id="element-139" data-mp="1d3xfty1cknxd">
                {season2Premiere.hook}
              </p>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-140" data-mp="18281a8ex1s4j">
                <a href={SPOTIFY_SHOW_URL} target="_blank" rel="noopener noreferrer" data-id="element-141" data-mp="25qi2wmasidv4">
                  <Button size="lg" leftIcon={<Play className="w-5 h-5 fill-current" data-id="element-143" data-mp="1vvq6vmwuvz2g" />} data-id="element-142" data-mp="1vtdiaqwleqgf">
                    {isLive ? 'Listen Now' : 'Follow to Get Notified'}
                  </Button>
                </a>
                {showDetailsLink && <Link to="/podcasts/season-2" data-id="element-144" data-mp="fevpmtaan4q0">
                    <Button size="lg" variant="outline" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-146" data-mp="2r0nx3k6zdpgo" />} data-id="element-145" data-mp="fe2hpozlm96a">
                      Read Jim's Story
                    </Button>
                  </Link>}
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-5 order-1 lg:order-2" data-id="element-147" data-mp="35sethjv697fz">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-dark-600" data-id="element-148" data-mp="3jrxrhju9r2ek">
              <img src={season2Premiere.image} alt="A man sitting alone on a rooftop at dusk overlooking a small town" className="w-full h-full object-cover" data-id="element-149" data-mp="3opzay5vlx3qk" />
              <div className="absolute inset-x-0 bottom-0 bg-dark-900/85 backdrop-blur-sm p-6" data-id="element-150" data-mp="1ueklzraf78bv">
                <p className="text-gold-500 text-sm font-semibold mb-1" data-id="element-151" data-mp="1mdv2ljxxekh1">
                  S2 · EP 1
                </p>
                <p className="text-white font-serif text-xl font-bold mb-1" data-id="element-152" data-mp="1w9ya75bjrbbd">
                  {season2Premiere.guest}
                </p>
                <p className="flex items-center text-white/60 text-sm" data-id="element-153" data-mp="1r9txzrho92qy">
                  <MapPin className="w-3.5 h-3.5 mr-1" data-id="element-154" data-mp="25a4m4mrlk38f" />
                  From {season2Premiere.hometown}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}